import { useEffect, useRef, useState } from 'react';

import { Search, X } from './icons';
import { cn } from './cn';

/**
 * Collapsible search control for the filter toolbar. Sits inline with the facet pills as a
 * round icon button and expands into a text field on click/tap (auto-focusing). It stays open
 * while it holds a query — showing the same accent treatment as an active facet — and collapses
 * back to the icon on blur or Escape once empty.
 */
export function SearchField({
  value,
  onChange,
  placeholder = 'Search title / key / description…',
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Open while the user has expanded it, or while a query keeps it pinned open.
  const open = expanded || value.length > 0;

  // Focus the field the moment it's opened by a click/tap.
  useEffect(() => {
    if (expanded) inputRef.current?.focus();
  }, [expanded]);

  return (
    <div
      className={cn(
        'inline-flex h-9 shrink-0 items-center overflow-hidden rounded-full border text-[13px] transition-[width,border-color,background-color] duration-200 ease-out',
        open ? 'w-[240px]' : 'w-9',
        value ? 'border-accent/40 bg-accent/10 text-fg' : 'border-line bg-surface-inset text-fg-muted',
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label="Search"
        aria-expanded={open}
        tabIndex={open ? -1 : 0}
        className={cn(
          'grid h-9 w-9 shrink-0 place-items-center transition-colors',
          value ? 'text-accent' : 'text-fg-faint hover:text-fg-soft',
          open && 'pointer-events-none',
        )}
      >
        <Search />
      </button>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          if (!value) setExpanded(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onChange('');
            setExpanded(false);
            inputRef.current?.blur();
          }
        }}
        placeholder={placeholder}
        aria-label="Search"
        tabIndex={open ? 0 : -1}
        className={cn(
          'h-9 min-w-0 flex-1 bg-transparent pr-1 text-fg placeholder:text-fg-ghost focus:outline-none',
          !open && 'pointer-events-none',
        )}
      />
      {value ? (
        <button
          type="button"
          onClick={() => {
            onChange('');
            setExpanded(true);
            inputRef.current?.focus();
          }}
          aria-label="Clear search"
          className="grid h-9 w-8 shrink-0 place-items-center text-fg-faint transition-colors hover:text-fg-soft"
        >
          <X />
        </button>
      ) : null}
    </div>
  );
}

export default SearchField;
